import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { firestore } from "../firebase.config"; // Ensure this path is correct
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { motion } from "framer-motion";
import Sidebar from "./Sidebar"; // Import Sidebar
import Loader from "./Loader"; // Import Loader if needed
import { saveAs } from "file-saver";
import { FaDownload } from "react-icons/fa";
import moment from "moment";
import jsPDF from "jspdf";
import logo from '../image/logo.png';
import autoTable from "jspdf-autotable";

const EditProductForm = () => {
  const location = useLocation();
  const { usertype } = location.state || {}; // Fallback if state is undefined
  const { productId } = useParams(); // Extract productId from URL
  const navigate = useNavigate(); // For navigation after actions
  // Initialize state with all required fields
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalImage, setModalImage] = useState(null);

  const toggleSidebar = (state) => {
    setIsSidebarOpen(state);
  };

    const cloudName = "dabhatz7m";
    const uploadPreset = "ImageUpload";

  const [productData, setProductData] = useState({
    OrderDate: "",
    Name: "",
    BookNumber: "",
    WorkDetails: "",
    WorkType: "",
    MobileNumber: "",
    Price: "",
    AdvanceAmount: "",
    EstimateOrderDate: "",
    imageUrls: [],
  });
  const [newImages, setNewImages] = useState([]); // Store newly selected files
  const [imagePreviews, setImagePreviews] = useState([]); // Store preview URLs
  const [uploadedImageUrls, setUploadedImageUrls] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0); // Add progress state

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    setNewImages(files);

    const previews = files.map((file) => URL.createObjectURL(file));
    setImagePreviews((prevPreviews) => [...prevPreviews, ...previews]);
  };

  const handleRemoveImage = (index) => {
    setImagePreviews((prevPreviews) => {
      const updatedPreviews = [...prevPreviews];
      updatedPreviews.splice(index, 1);
      return updatedPreviews;
    });

    setNewImages((prevImages) => {
      const updatedImages = [...prevImages];
      // Only remove from newImages if the index is within the range of new images
      if (index >= productData.imageUrls.length) {
        updatedImages.splice(index - productData.imageUrls.length, 1);
      }
      return updatedImages;
    });

    setProductData((prevData) => {
      const updatedImageUrls = [...prevData.imageUrls];
      // Remove from the correct array (either existing imageUrls or newImages)
      if (index < prevData.imageUrls.length) {
        updatedImageUrls.splice(index, 1);
      }
      return { ...prevData, imageUrls: updatedImageUrls };
    });
  };

  const handleImageUpload = async () => {
    setIsLoading(true);
    try {
      const uploadPromises = newImages.map(async (file) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("upload_preset", uploadPreset);
        formData.append('folder', 'OrderImage'); // Store in "gallery" folder in Cloudinary

        const xhr = new XMLHttpRequest();
        xhr.open(
          "POST",
          `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`,
          true
        );

        return new Promise((resolve, reject) => {
          xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
              const percentComplete = (event.loaded / event.total) * 100;
              setUploadProgress(percentComplete);
            }
          };

          xhr.onload = () => {
            if (xhr.status === 200) {
              const data = JSON.parse(xhr.responseText);
              if (data.secure_url) {
                resolve(data.secure_url);
              } else {
                reject(
                  new Error("Secure URL is missing from Cloudinary response")
                );
              }
            } else {
              reject(new Error("Failed to upload image to Cloudinary"));
            }
          };

          xhr.onerror = () => {
            reject(new Error("Error uploading image: " + xhr.statusText));
          };

          xhr.send(formData);
        });
      });

      const newImageUrls = await Promise.all(uploadPromises);
      setUploadedImageUrls(newImageUrls);
      setImagePreviews([...imagePreviews, ...newImageUrls]);
      setNewImages([]);
      setUploadProgress(0); // Reset progress
      setIsLoading(false);
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Error uploading images. Please try again.");
      setUploadProgress(0); // Reset progress
      setIsLoading(false);
    }
  };

  // State for notifications
  const [field, setField] = useState(false);
  const [alertStatus, setAlertStatus] = useState("danger");
  const [msg, setMsg] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch product data when the component mounts
  useEffect(() => {
    const fetchProductData = async () => {
      setIsLoading(true);
      try {
        const productDocRef = doc(firestore, "Products", productId);
        const productSnapshot = await getDoc(productDocRef);
        if (productSnapshot.exists()) {
          const data = productSnapshot.data();
          setProductData({ id: productSnapshot.id, ...productSnapshot.data() });
          setImagePreviews(data.imageUrls || []);
          setIsLoading(false);
        } else {
          toast.error("Order not found.");
          navigate("/"); // Redirect to home or another appropriate page
        }
      } catch (error) {
        console.error("Error fetching order data:", error);
        toast.error("Failed to fetch order data. Please try again.");
        setIsLoading(false);
      }
    };

    fetchProductData();
  }, [productId, navigate]);

  // Handle input changes for all form fields
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProductData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Handle changes in the ReactQuill editor
  const handleJobDescriptionChange = (value) => {
    setProductData((prevData) => ({
      ...prevData,
      WorkDetails: value,
    }));
  };
  useEffect(() => {
    setProductData((prevData) => ({
      ...prevData,
      PendingAmount: prevData.Price - prevData.AdvanceAmount,
    }));
  }, [productData.Price, productData.AdvanceAmount]);

  // Handle form submission to update the product data
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      // Validate required fields
      const {
        OrderDate,
        Name,
        BookNumber,
        WorkDetails,
        WorkType,
        MobileNumber,
        Price,
        AdvanceAmount,
        EstimateOrderDate,
        imageUrls,
      } = productData;

      if (
        !OrderDate ||
        !Name ||
        !BookNumber ||
        !WorkDetails ||
        !WorkType ||
        !MobileNumber ||
        !Price ||
        !AdvanceAmount ||
        !EstimateOrderDate
      ) {
        setField(true);
        setMsg("Required fields can't be empty");
        setAlertStatus("danger");
        setIsLoading(false);
        setTimeout(() => {
          setField(false);
        }, 4000);
        return;
      }

      const productDocRef = doc(firestore, "Products", productId);
      const allImageUrls = [...productData.imageUrls, ...uploadedImageUrls];

      await updateDoc(productDocRef, {
        ...productData, // Update other product data
        imageUrls: allImageUrls, // Update imageUrls in Firestore
      });

      setUploadedImageUrls([]); // Clear uploaded URLs after saving
      setImagePreviews(allImageUrls); // Update previews to reflect saved data

      setField(true);
      setMsg("Order updated successfully!");
      setAlertStatus("success");
      setIsLoading(false);
      setTimeout(() => {
        setField(false);
        navigate("/viewItems");
      }, 3000);
    } catch (error) {
      console.error("Error updating order:", error);
      setField(true);
      setMsg("Failed to update order. Please try again.");
      setAlertStatus("danger");
      setIsLoading(false);
      setTimeout(() => {
        setField(false);
      }, 4000);
    }
  };
  const handleDownloadImage = (imageUrl) => {
    saveAs(imageUrl, `image-${Date.now()}`);
  };
  const uploadToCloudinary = async (pdfBlob) => {
    if (!pdfBlob) {
      console.error("PDF Blob is null or undefined");
      toast.error("Invalid file. Please try again.");
      return null;
    }
  
    const formData = new FormData();
    formData.append("file", pdfBlob, `bill-${productData.BookNumber}.pdf`);
    formData.append("upload_preset", uploadPreset); // Ensure it's correct
  
    try {
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${cloudName}/raw/upload`,
        {
          method: "POST",
          body: formData,
        }
      );
  
      if (!response.ok) {
        const errorData = await response.json(); // Get Cloudinary error details
        console.error("Cloudinary Error:", errorData);
        throw new Error(errorData.error.message || "Failed to upload to Cloudinary");
      }
  
      const data = await response.json();
      return data.secure_url;
    } catch (error) {
      console.error("Cloudinary upload error:", error);
      toast.error("Failed to upload bill to Cloudinary.");
      return null;
    }
  };
  
  const generateBill = async () => {
    if (!productData) return;
  
    const doc = new jsPDF();
  
    // Add logo (replace 'logo' with your image data)
    const imgWidth = 50;
    const imgHeight = 20;
    // Assuming 'logo' is your image data
    doc.addImage(logo, "PNG", 15, 10, imgWidth, imgHeight);
  // Address on the left side (Added extra space below the logo)
  doc.setFontSize(10);
  doc.setTextColor(255, 0, 0); // Red color
  const addressY = 35; // Increased Y position for space
  doc.text("In Front of SBI, HDFC Bank, Main Road, Digapahandi - 761012", 15, addressY);

 // Define positions
const textX = 160; // Shift text slightly left for proper alignment
const lineX = textX - 5; // Vertical line just to the left of the text
const jobY = addressY; // Job No. aligns with Address
const mobileY = addressY - 10; // Mobile No. above Job No.

// Set text properties
doc.setFontSize(10);
doc.setTextColor(255, 0, 0); // Black color for text

// Draw the vertical line beside the text
doc.setDrawColor(255, 0, 0); // Red color for the vertical line
doc.setLineWidth(0.5); // Adjust thickness if needed
doc.line(lineX, mobileY - 3, lineX, jobY + 3); // Short vertical line

// Add text (Positioned correctly beside the vertical line)
const jobText = `Job No.: ${productData.BookNumber}`;
doc.text("+918114667112", textX, mobileY);
doc.text(jobText, textX, jobY);

// Reset text and draw color to black
doc.setTextColor(0, 0, 0);
doc.setDrawColor(0, 0, 0);
// ✅ ADD EXTRA SPACE BELOW (Increase next content's Y position)
const extraSpacing = 6; // Adjust as needed
const nextContentY = addressY + extraSpacing; // Add space

    // Function to draw a red underline
    const drawRedUnderline = (doc, text, x, y) => {
      const textWidth = doc.getTextWidth(text); // Get dynamic text width
      doc.setDrawColor(255, 0, 0); // Set red color for the underline
      doc.line(x, y, x + textWidth, y); // Draw underline with text width
      doc.setDrawColor(0, 0, 0); // Reset to black
      doc.setTextColor(0, 0, 0); // Reset color to black
    };
    
    doc.setFontSize(12);
    doc.setTextColor(255, 0, 0);
    const customerText = "Customer Details:";
    doc.text(customerText, 15, nextContentY);
    drawRedUnderline(doc, customerText, 15, nextContentY + 3);

    doc.setFontSize(10);
    doc.text("Name:", 15, 50);
    doc.text(String(productData.Name), 50, 50);
    doc.text("Mobile No.:", 15, 60);
    doc.text(String(productData.MobileNumber), 50, 60);
    doc.text("O. Date:", 15, 70);
    doc.text(moment(productData.OrderDate).format("DD/MM/YYYY"), 50, 70);
    doc.text("D.Date:", 100, 70);
    doc.text(moment(productData.EstimateOrderDate).format("DD/MM/YYYY"), 135, 70);
  
    doc.setFontSize(12);
    doc.setTextColor(255, 0, 0); // Red color for heading
    const paymentText = "Payment Details:";
    doc.text(paymentText, 15, 90);
    drawRedUnderline(doc, paymentText, 15, 93); // Dynamic width underline
    
    doc.setFontSize(10);
    doc.text("Total Amount:", 15, 100);
    doc.text(`${productData.Price} Rs`, 50, 100);
    doc.text("Advance Amount:", 15, 110);
    doc.text(`${productData.AdvanceAmount} Rs`, 50, 110);
    doc.text("D.Date:", 80, 110);
    doc.text(moment(productData.OrderDate).format("DD/MM/YYYY"), 100, 110);
    doc.text("Rest Amount:", 15, 120);
    doc.text(`${productData.PendingAmount} Rs`, 50, 120);
    doc.text("D.Date:", 80, 120);
    doc.text(moment(productData.EstimateOrderDate).format("DD/MM/YYYY"), 100, 120);
  // Define table columns and rows
const tableColumn = ["Work Type", "Details", "Amount"];
const tableRows = [];

// Extract product details into table rows
const plainWorkDetails = String(productData.WorkDetails).replace(/<[^>]*>?/gm, ""); // Remove HTML tags if any
tableRows.push([productData.WorkType, plainWorkDetails, `${productData.Price} Rs`]);

// Add the Total row to the table
tableRows.push(["", "Total", `${productData.Price} Rs`]); // Empty Work Type column for alignment
autoTable(doc, {
  startY: 140,
  head: [tableColumn],
  body: tableRows,
  theme: "grid",
  styles: { fontSize: 10 },
  headStyles: { fillColor: [255, 0, 0] },
  alternateRowStyles: { fillColor: [240, 240, 240] },
  didParseCell: function (data) {
    // Apply red color to "Total" text
    if (data.row.index === tableRows.length - 1 && data.column.index === 1) {
      data.cell.styles.textColor = [255, 0, 0]; // Red color
      data.cell.styles.fontStyle = "bold"; // Make it bold
    }
  }
});

// ✅ Ensure finalY is defined
const finalY = doc.lastAutoTable?.finalY ? doc.lastAutoTable.finalY + 10 : 150;

// // Draw red line below the table
// doc.setDrawColor(255, 0, 0);
// doc.line(15, finalY, 185, finalY);

// Add Terms & Conditions
const finalTextY = finalY + 10;
// Increase top space before text (adjust as needed)
const topSpacing = -20; // Add extra space above text

// Set text color to a darker blue
doc.setTextColor(0, 102, 204); // Darker blue shade

doc.text("1. Terms & Condition Applied.", 15, finalTextY + topSpacing + 10);
doc.text("2. No Warranty, No Guarantee.", 15, finalTextY + topSpacing + 15);
doc.text("3. No Advance, No Work.", 15, finalTextY + topSpacing + 20);
doc.text("4. 85% Advance Payment Required for work.", 15, finalTextY + topSpacing + 25);
doc.text("etc.", 19, finalTextY + topSpacing + 30);

// Reset text color back to black for other elements
doc.setTextColor(0, 0, 0);
// Add signature section with minimal spacing
doc.setFontSize(10);
doc.text("Auth Sign", 150, finalTextY + 25); 
doc.text(".....................", 170, finalTextY + 25); // Closer to "Auth Sign"

// Save the PDF
doc.save(`bill-${productData.BookNumber}.pdf`);
try {
  const pdfBlob = doc.output('blob');
  const cloudinaryUrl = await uploadToCloudinary(pdfBlob);

  if (cloudinaryUrl) {
      const productDocRef = doc(firestore, "Products", productId);
      await updateDoc(productDocRef, {
          pdfBillUrl: cloudinaryUrl,
      });
      saveAs(pdfBlob, `bill-${productData.BookNumber}.pdf`);
  //    toast.success("Bill generated, uploaded, and saved successfully!");
  }
  setIsLoading(false);
} catch (error) {
  console.error("Error generating or saving bill:", error);
//  toast.error("Failed to generate or save bill.");
  setIsLoading(false);
}
};


  return (
    <div className="flex min-h-screen bg-white">
      <Sidebar
        isOpen={isSidebarOpen}
        toggleSidebar={toggleSidebar}
        userType={usertype}
      />

      <div
        className={`flex-1 lg:p-8  p-2 overflow-y-auto transition-all lg:mt-0 mt-10 duration-300 ${
          isSidebarOpen ? "ml-56" : "lg:ml-20 sm:ml-2"
        }`}
      >
        <div
          className={`max-w-4xl mx-auto bg-white shadow-md rounded-lg  ${
            isSidebarOpen ? "text-sm p-0" : "lg:p-6 p-6"
          }`}
        >
          {field && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className={`w-full p-4 mb-6 rounded-lg text-center text-lg font-semibold ${
                alertStatus === "danger"
                  ? "bg-red-100 text-red-700"
                  : "bg-green-100 text-green-700"
              }`}
            >
              {msg}
            </motion.div>
          )}

          <h2
            className={`lg:text-2xl font-semibold mb-6 text-gray-800 ${
              isSidebarOpen ? "text-[13px]" : ""
            }`}
          >
            Edit Order Details
          </h2>

          <form onSubmit={handleFormSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex flex-col">
                <label
                  htmlFor="OrderDate"
                  className={`mb-2 font-medium text-gray-700 ${
                    isSidebarOpen ? "text-xs" : ""
                  }`}
                >
                  Order Date
                </label>
                <input
                  id="OrderDate"
                  name="OrderDate"
                  type="date"
                  className={`lg:px-4 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-200 ${
                    isSidebarOpen ? "px-1" : ""
                  }`}
                  value={productData.OrderDate}
                  placeholder="Enter Order Date"
                  onChange={handleInputChange}
                />
              </div>
              <div className="flex flex-col">
                <label
                  htmlFor={`BookNumber' className='mb-2 font-medium text-gray-700 ${
                    isSidebarOpen ? "text-xs" : ""
                  }`}
                >
                  job Number
                </label>
                <input
                  id="BookNumber"
                  name="BookNumber"
                  type="text"
                  className={`lg:px-4 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-200 ${
                    isSidebarOpen ? "px-1" : ""
                  }`}
                  value={productData.BookNumber}
                  placeholder="Enter job Number"
                  onChange={handleInputChange}
                />
              </div>

              <div className="flex flex-col">
                <label
                  htmlFor="MobileNumber"
                  className={`mb-2 font-medium text-gray-700 ${
                    isSidebarOpen ? "text-xs" : ""
                  }`}
                >
                  Customer Mobile Number
                </label>
                <input
                  id="MobileNumber"
                  name="MobileNumber"
                  type="number"
                  className={`lg:px-4 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-200 ${
                    isSidebarOpen ? "px-1" : ""
                  }`}
                  value={productData.MobileNumber}
                  placeholder="Enter Customer Mobile Number"
                  onChange={handleInputChange}
                />
              </div>
              <div className="flex flex-col">
                <label
                  htmlFor="Name"
                  className={`mb-2 font-medium text-gray-700 ${
                    isSidebarOpen ? "text-xs" : ""
                  }`}
                >
                  Customer Name
                </label>
                <input
                  id="Name"
                  name="Name"
                  type="text"
                  className={`lg:px-4 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-200 ${
                    isSidebarOpen ? "px-1" : ""
                  }`}
                  value={productData.Name}
                  placeholder="Enter Customer Name"
                  onChange={handleInputChange}
                />
              </div>
              <div className="flex flex-col">
                <label
                  htmlFor="Price"
                  className={`mb-2 font-medium text-gray-700 ${
                    isSidebarOpen ? "text-xs" : ""
                  }`}
                >
                  Total Price
                </label>
                <input
                  id="Price"
                  name="Price"
                  type="number"
                  className={`lg:px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-200 ${
                    isSidebarOpen ? "px-1" : ""
                  }`}
                  value={productData.Price}
                  placeholder="Total Price"
                  onChange={handleInputChange}
                  onKeyDown={(e) => {
                    if (e.key === "-" || e.key === "e") {
                      e.preventDefault(); // Prevent typing '-' or 'e' (exponential notation)
                    }
                  }}
                />
              </div>

              <div className="flex flex-col">
                <label
                  htmlFor="AdvanceAmount"
                  className={`mb-2 font-medium text-gray-700 ${
                    isSidebarOpen ? "text-xs" : ""
                  }`}
                >
                  Advance Amount
                </label>
                <input
                  id="AdvanceAmount"
                  name="AdvanceAmount"
                  type="number"
                  className={`lg:px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-200 ${
                    isSidebarOpen ? "px-1" : ""
                  }`}
                  value={productData.AdvanceAmount}
                  placeholder="Advance Amount"
                  onChange={handleInputChange}
                  onKeyDown={(e) => {
                    if (e.key === "-" || e.key === "e") {
                      e.preventDefault(); // Prevent typing '-' or 'e' (exponential notation)
                    }
                  }}
                />
              </div>
              <div className="flex flex-col">
                <label
                  htmlFor="PendingAmount"
                  className={`mb-2 font-medium text-gray-700 ${
                    isSidebarOpen ? "text-xs" : ""
                  }`}
                >
                  Pending Amount
                </label>
                <input
                  id="PendingAmount"
                  name="PendingAmount"
                  type="number"
                  className={`lg:px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-200 ${
                    isSidebarOpen ? "px-1" : ""
                  }`}
                  value={productData.PendingAmount}
                  placeholder="Pending Amount"
                  onChange={handleInputChange}
                />
              </div>

              <div className="flex flex-col">
                <label
                  htmlFor="id"
                  className={`mb-2 font-medium text-gray-700 ${
                    isSidebarOpen ? "text-xs" : ""
                  }`}
                >
                  Order ID
                </label>
                <input
                  id="id"
                  name="id"
                  type="text"
                  value={productData.id}
                  readOnly
                  className={`lg:px-4 px-3 py-2 bg-gray-100 border border-gray-200 rounded-md cursor-not-allowed ${
                    isSidebarOpen ? "px-1" : ""
                  }`}
                />
              </div>
              <div className="flex flex-col">
                <label
                  htmlFor="EstimateOrderDate"
                  className={`mb-2 font-medium text-gray-700 ${
                    isSidebarOpen ? "text-xs" : ""
                  }`}
                >
                  Estimate Order Date
                </label>
                <input
                  id="EstimateOrderDate"
                  name="EstimateOrderDate"
                  type="date"
                  value={productData.EstimateOrderDate}
                  placeholder="Estimate Order Date"
                  className={`lg:px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-200 ${
                    isSidebarOpen ? "px-1" : ""
                  }`}
                />
              </div>
              <div className="flex flex-col">
                <label
                  htmlFor="WorkType"
                  className={`mb-2 font-medium text-gray-700 ${
                    isSidebarOpen ? "text-xs" : ""
                  }`}
                >
                  Work Type
                </label>
                
                <select
                id='WorkType'
                name="WorkType"
                value={productData.WorkType}
                autoComplete='off'
                placeholder='Enter Work Type'
                className={`w-full px-4 py-2 border border-gray-300 lg:text-md text-sm rounded-lg text-gray-800 focus:ring-2 focus:ring-blue-500 focus:outline-none ${isSidebarOpen ? 'text-xs' : ''}`}
                onChange={handleInputChange} // Add onChange handler
            >
                <option value=''>Select Work Type</option>
                <option value='FLEX NORMAL'>FLEX NORMAL</option>
                <option value='FLEX HOT'>FLEX HOT</option>
                <option value='FLEX STAR'>FLEX STAR</option>
                <option value='FLEX BLACK BACK'>FLEX BLACK BACK</option>
                <option value='FLEX VINYL'>FLEX VINYL</option>
                <option value='FLEX ONE WAY VISION'>FLEX ONE WAY VISION</option>
                <option value='FLEX CANVAS'>FLEX CANVAS</option>
                <option value='FLEX SUNBOARD PASTING'>FLEX SUNBOARD PASTING</option>
                <option value='FLEX LIGHT BOARD'>FLEX LIGHT BOARD</option>
                <option value='CUP 6OZ'>CUP 6OZ</option>
                <option value='CUP 11OZ'>CUP 11OZ</option>
                <option value='CUP 11OZ MAGIC'>CUP 11OZ MAGIC</option>
                <option value='BILL BOOK B24'>BILL BOOK B24</option>
                <option value='BILL BOOK B16'>BILL BOOK B16</option>
                <option value='BILL BOOK B12'>BILL BOOK B12</option>
                <option value='BILL BOOK B8'>BILL BOOK B8</option>
                <option value='BILL BOOK B6'>BILL BOOK B6</option>
                <option value='BILL BOOK B4'>BILL BOOK B4</option>
                <option value='BILL BOOK A3'>BILL BOOK A3</option>
                <option value='BILL BOOK A4'>BILL BOOK A4</option>
                <option value='BILL BOOK A5'>BILL BOOK A5</option>
                <option value='BILL BOOK A5 VR'>BILL BOOK A5 VR</option>
                <option value='BILL BOOK A6'>BILL BOOK A6</option>
                <option value='LEAFLET A3'>LEAFLET A3</option>
                <option value='LEAFLET A4'>LEAFLET A4</option>
                <option value='LEAFLET A5'>LEAFLET A5</option>
                <option value='LEAFLET A6'>LEAFLET A6</option>
                <option value="POSTER_HALF_DEMY">POSTER HALF DEMY</option>
                <option value="POSTER_FULL_DEMY">POSTER FULL DEMY</option>
                <option value="CALENDAR_DIGITAL_12X18">CALENDAR DIGITAL 12 X 18</option>
                <option value="CALENDAR_TABLE_TOP_A4_WIRO">CALENDAR TABLE TOP A4 WIRO</option>
                <option value="CALENDAR_TABLE_TOP_A5_WIRO">CALENDAR TABLE TOP A5 WIRO</option>
                <option value="CALENDAR_TABLE_TOP_A6_WIRO">CALENDAR TABLE TOP A6 WIRO</option>
                <option value="WHITE_TSHIRT_PRINT">WHITE T-SHIRT PRINT</option>
                <option value="BLACK_TSHIRT_PRINT">BLACK T-SHIRT PRINT</option>
                <option value="BIRTHDAY_CARD">BIRTHDAY CARD</option>
                <option value="INVITATION_CARD">INVITATION CARD</option>
                <option value="ID_CARD_I1">ID CARD I1</option>
                <option value="ID_CARD_I2">ID CARD I2</option>
                <option value="ID_CARD_I3">ID CARD I3</option>
                <option value="ID_CARD_I4">ID CARD I4</option>
                <option value="ID_CARD_I5">ID CARD I5</option>
                <option value="ID_CARD_I6">ID CARD I6</option>
                <option value="ID_CARD_I7">ID CARD I7</option>
                <option value="ID_CARD_I8">ID CARD I8</option>
                <option value="ID_CARD_I9">ID CARD I9</option>
                <option value="ID_CARD_I10">ID CARD I10</option>
                <option value="ID_CARD_I11">ID CARD I11</option>
                <option value="ID_CARD_I12">ID CARD I12</option>
                <option value="ID_CARD_I13">ID CARD I13</option>
                <option value="ID_CARD_I14">ID CARD I14</option>
                <option value="ID_CARD_I15">ID CARD I15</option>
                <option value="VISITING_CARD_SS">VISITING CARD SS</option>
                <option value="VISITING_CARD_SS_L">VISITING CARD SS L</option>
                <option value="VISITING_CARD_DS">VISITING CARD DS</option>
                <option value="VISITING_CARD_DS_L">VISITING CARD DS L</option>
                <option value="VISITING_CARD_SN_SYN">VISITING CARD SN SYN</option>
                <option value="VISITING_CARD_DS_SYN">VISITING CARD DS SYN</option>
                <option value="MENU_CARD_SS">MENU CARD SS</option>
                <option value="MENU_CARD_DS">MENU CARD DS</option>
                <option value="MENU_CARD_A4_FOLD">MENU CARD A4 FOLD</option>
                <option value="MENU_CARD_A5_FOLD">MENU CARD A5 FOLD</option>
                <option value="MENUCARD_A4_WIRO">MENUCARD A4 WIRO</option>
                <option value="MENUCARD_A5_WIRO">MENUCARD A5 WIRO</option>
                <option value="PREINK_STAMP_SQR">PREINK STAMP SQR</option>
                <option value="PREINK_STAMP_RND">PREINK STAMP RND</option>
                <option value="LETTER_PAD_A5_NORMAL">LETTER PAD A5 NORMAL</option>
                <option value="LETTER_PAD_A5_BOND">LETTER PAD A5 BOND</option>
                <option value="LETTER_PAD_A4_NORMAL">LETTER PAD A4 NORMAL</option>
                <option value="LETTER_PAD_A4_BOND">LETTER PAD A4 BOND</option>
                <option value="CERTIFICATE_SN_COLOR">CERTIFICATE SN COLOR</option>
                <option value="CERTIFICATE_DUAL_COLOR">CERTIFICATE DUAL COLOR</option>
                <option value="CERTIFICATE_MULTI_COLOR">CERTIFICATE MULTI COLOR</option>
                <option value="PHOTO_FRAME_4x4">PHOTO FRAME 4 x 4 in</option>
                <option value="PHOTO_FRAME_6x4">PHOTO FRAME 6 X 4 in</option>
                <option value="PHOTO_FRAME_6x8">PHOTO FRAME 6 X 8 in</option>
                <option value="PHOTO_FRAME_6x18">PHOTO FRAME 6 X 18 in</option>
                <option value="PHOTO_FRAME_10x8">PHOTO FRAME 10 X 8 in</option>
                <option value="PHOTO_FRAME_12x8">PHOTO FRAME 12 X 8 in</option>
                <option value="PHOTO_FRAME_12x8_5">PHOTO FRAME 12 X 8.5 in</option>
                <option value="PHOTO_FRAME_12x18">PHOTO FRAME 12 X 18 in</option>
                <option value="PHOTO_FRAME_2FTx2FT">PHOTO FRAME 2FT X 2 ft</option>
                <option value="PHOTO_FRAME_2FTx3FT">PHOTO FRAME 2FT X 3 ft</option>
                <option value="MIXTURE_LABEL_1">MIXTURE LABEL 1</option>
                <option value="MIXTURE_LABEL_2">MIXTURE LABEL 2</option>
                <option value="MIXTURE_LABEL_3">MIXTURE LABEL 3</option>
                <option value="MIXTURE_LABEL_4">MIXTURE LABEL 4</option>
                <option value="MIXTURE_LABEL_5">MIXTURE LABEL 5</option>
                <option value="CAP_DTF">CAP DTF</option>
                <option value="CAP SUBLIMATION">CAP SUBLIMATION</option>
                <option value="NON-WOVEN BAG A">NON-WOVEN BAG A</option>
                <option value="NON-WOVEN BAG B">NON-WOVEN BAG B</option>
                <option value="NON-WOVEN BAG C">NON-WOVEN BAG C</option>
                <option value="NON-WOVEN BAG D">NON-WOVEN BAG D</option>
                <option value="NON-WOVEN BAG E">NON-WOVEN BAG E</option>
                <option value="NON-WOVEN BAG DS A">NON-WOVEN BAG DS A</option>
                <option value="NON-WOVEN BAG DS B">NON-WOVEN BAG DS B</option>
                <option value="NON-WOVEN BAG DS C">NON-WOVEN BAG DS C</option>
                <option value="NON-WOVEN BAG DS D">NON-WOVEN BAG DS D</option>
                <option value="NON-WOVEN BAG DS E">NON-WOVEN BAG DS E</option>
                <option value="KEY RING">KEY RING</option>
                <option value="PHOTO 12 X 18 in">PHOTO 12 X 18 in</option>
                <option value="PHOTO STICKER 12 X 18 in">PHOTO STICKER 12 X 18 in</option>
                <option value="PHOTO STICKER 12 X 18 in WITH CUT">PHOTO STICKER 12 X 18 in WITH CUT</option>
                <option value="PHOTO 2ft X 1ft">PHOTO 2ft X 1ft</option>
                <option value="PHOTO 2ft X 2ft">PHOTO 2ft X 2ft</option>
                <option value="PHOTO 2ft X 3ft">PHOTO 2ft X 3ft</option>

              </select>
              </div>
            </div>
           
            <div className="flex flex-col">
              <label
                className={`mb-2 font-medium text-gray-700 ${
                  isSidebarOpen ? "text-xs" : ""
                }`}
                htmlFor="WorkDetails"
              >
                Work Details
              </label>
              <ReactQuill
                id="WorkDetails"
                value={productData?.WorkDetails || ""}
                onChange={handleJobDescriptionChange}
                placeholder="Enter Work Details"
                className="h-36 md:h-64 lg:h-72 rounded-md border border-gray-300 mb-10"
                modules={{
                  toolbar: [
                    [{ header: "1" }, { header: "2" }, { font: [] }],
                    ["bold", "italic", "underline"],
                    ["link", "image"],
                    [{ list: "ordered" }, { list: "bullet" }],
                    ["clean"],
                  ],
                }}
              />
            </div>

            <div className="flex items-center space-x-2">
              <label
                htmlFor="imageInput"
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer"
              >
                <svg
                  className="mr-1 h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>Choose Image</span>
              </label>
              <input
                type="file"
                id="imageInput"
                multiple
                className="hidden"
                onChange={handleImageChange}
              />
              <button
                onClick={handleImageUpload}
                disabled={isLoading}
                className="px-3 py-1.5 bg-blue-600 text-white rounded-md text-xs hover:bg-blue-700 transition disabled:opacity-50"
              >
                Upload Image
              </button>
              {uploadProgress > 0 && (
                <div className="mt-2">
                  <div className="bg-gray-200 rounded-full h-2.5 mb-4 dark:bg-gray-700">
                    <div
                      className="bg-blue-600 h-2.5 rounded-full"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-gray-600 text-center">
                    {Math.round(uploadProgress)}%
                  </p>
                </div>
              )}
            </div>
            {/* Display Image Previews (Now including existing images) */}
            <div className="flex flex-wrap mt-2">
              {imagePreviews.map((preview, index) => (
                <div key={index} className="relative m-1">
                  <img
              src={preview}
              alt={`Product Image ${index + 1}`}
              className="w-24 h-24 object-cover rounded cursor-pointer"
              onClick={() => {
                setModalImage(preview);
                setModalOpen(true);
              }}
            />
                  <button
                    type="button"
                    onClick={() => handleRemoveImage(index)} // Now correctly calls the function
                    className="absolute top-1 right-1 bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-2 rounded-full text-xs"
                  >
                    X
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDownloadImage(preview)} // Correctly implemented here
                    className="absolute top-1 left-1 bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-2 rounded-full text-xs"
                  >
                    <FaDownload />
                  </button>
                </div>
              ))}
            </div>
            {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
          <div className="relative bg-white rounded-lg shadow-lg overflow-hidden w-[600px] h-[500px]"> {/* Set fixed width and height */}
            <img
              src={modalImage}
              alt="Modal Image"
              className="block w-full h-full object-contain"
            />
            <button
              onClick={() => setModalOpen(false)}
              className="absolute top-2 right-2 p-2 bg-gray-200 rounded-full hover:bg-gray-300 transition duration-200"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      )}
            <div className="flex justify-end space-x-4">
            <button
            type="button"
            onClick={generateBill}
            className="flex items-center px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition disabled:opacity-50"
          >
            Generate Bill
          </button>
              <button
                type="submit"
                className={`flex items-center px-6 py-2 bg-blue-600 text-white  rounded-md hover:bg-blue-700 transition disabled:opacity-50 ${
                  isSidebarOpen ? "mt-24" : "lg:mt-0 mt-20"
                }`}
                disabled={isLoading}
              >
                {isLoading ? <Loader /> : "Update Order"}
              </button>
            </div>
          </form>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default EditProductForm;
